import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useJ514, ItemState } from './hooks/useJ514';
import { j514Api } from '../../services/j514Api';
import { BatchAcceptanceBridge, pwj514f1AcceptanceManifest } from '../../features/acceptance';
import './J514Form.css';

// Helpers
const fmtNumber = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const J514Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  } = useJ514();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  // Auto-load all records on mount
  React.useEffect(() => {
    handleQuery('', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆研發採購單？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const allSelected =
    items.length > 0 && items.every((s) => s.selected || !s.item.can_approve);

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected && items.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={items.length === 0}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          disabled={!record.item.can_approve}
          onChange={(e) =>
            handleToggleItem(record.item.fj510_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: <span data-contract-field="fj510_01">採購單號</span>,
      dataIndex: ['item', 'fj510_01'],
      key: 'fj510_01',
      width: 120,
    },
    {
      title: <span data-contract-field="fj510_11">採購日期</span>,
      dataIndex: ['item', 'fj510_11'],
      key: 'fj510_11',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: <span data-contract-field="fj510_12">供應商</span>,
      dataIndex: ['item', 'supplier_name'],
      key: 'supplier_name',
      width: 160,
    },
    {
      title: <span data-contract-field="fj510_13">產品代號</span>,
      dataIndex: ['item', 'product_code'],
      key: 'product_code',
      width: 140,
    },
    {
      title: <span data-contract-field="fj510_21">交貨日期</span>,
      dataIndex: ['item', 'fj510_21'],
      key: 'fj510_21',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: <span data-contract-field="fj510_29">採購金額</span>,
      dataIndex: ['item', 'fj510_29'],
      key: 'fj510_29',
      width: 120,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: <span data-contract-field="fj510_80">狀態</span>,
      key: 'status',
      width: 80,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        const s = record.item.fj510_80;
        if (s === 'P') return <Tag color="success">已核准</Tag>;
        if (s && s !== '')
          return <Tag color="warning">等級 {s}</Tag>;
        return <Tag>待核准</Tag>;
      },
    },
  ];

  return (
    <div className="j514-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                研發管理
              </a>
              <span>/</span>
              <span className="current">研發採購單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>研發採購單核准</h1>
            <span className="badge">J514</span>
          </div>
        </div>

        <div className="query-bar">
          <label data-contract-field="fj510_01">採購單 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fj510_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入採購單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
          >
            核准 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwj514f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fj510_01)}
          keyOf={(item) => item.fj510_01}
          queryExact={async (key) => (await j514Api.query(key, key)).items?.[0] || null}
          lastApiResponse={items}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items[0];
            if (first) handleToggleItem(first.item.fj510_01, true);
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="只選取正式 API 回傳的第一筆研發採購核准候選。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default J514Form;
